import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, orderBy, query } from 'firebase/firestore';
import { EmptyState } from '../components/EmptyState';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Field, TextArea, TextInput } from '../components/ui/Field';
import { FormPanel, PageHeader, RecordGroup, SearchField } from '../components/ui/Page';
import { Switch } from '../components/ui/Switch';
import { ReadFailure } from '../components/ReadFailure';
import { listenQuery } from '../lib/liveRead';
import { useAuth } from '../auth/AuthProvider';
import { db, functions } from '../lib/firebase';
import { formatTimestamp } from '../lib/format';
import { canManageCompany, type SubcontractorRecord } from '../types/models';

type SubcontractorForm = {
  name: string;
  shortName: string;
  contactName: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
  isActive: boolean;
};

const EMPTY_FORM: SubcontractorForm = {
  name: '',
  shortName: '',
  contactName: '',
  email: '',
  phone: '',
  address: '',
  notes: '',
  isActive: true,
};

export default function SubcontractorsPage() {
  const { activeCompanyId, activeRole } = useAuth();
  const [subcontractors, setSubcontractors] = useState<SubcontractorRecord[]>(
    []
  );
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<SubcontractorRecord | null>(null);
  const [form, setForm] = useState<SubcontractorForm>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [readAttempt, setReadAttempt] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);

  const canManage = canManageCompany(activeRole ?? undefined);

  useEffect(() => {
    if (!activeCompanyId) {
      setSubcontractors([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setSubcontractors([]);
    setError(null);
    setReadError(null);
    const ref = collection(db, 'companies', activeCompanyId, 'subcontractors');
    const q = query(ref, orderBy('name'));
    const unsub = listenQuery(
      q,
      (snap) => {
        setSubcontractors(
          snap.docs.map((subcontractorDoc) => ({
            id: subcontractorDoc.id,
            ...(subcontractorDoc.data() as Omit<SubcontractorRecord, 'id'>),
          }))
        );
        setReadError(null);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setReadError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [activeCompanyId, readAttempt]);

  const filteredSubcontractors = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return subcontractors;
    return subcontractors.filter((subcontractor) =>
      [
        subcontractor.name,
        subcontractor.shortName,
        subcontractor.contactName,
        subcontractor.email,
        subcontractor.phone,
        subcontractor.address,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [search, subcontractors]);

  const activeSubcontractors = filteredSubcontractors.filter(
    (subcontractor) => subcontractor.isActive !== false
  );
  const archivedSubcontractors = filteredSubcontractors.filter(
    (subcontractor) => subcontractor.isActive === false
  );

  function startNew() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setNotice(null);
  }

  function startEdit(subcontractor: SubcontractorRecord) {
    setEditing(subcontractor);
    setForm({
      name: subcontractor.name ?? '',
      shortName: subcontractor.shortName ?? '',
      contactName: subcontractor.contactName ?? '',
      email: subcontractor.email ?? '',
      phone: subcontractor.phone ?? '',
      address: subcontractor.address ?? '',
      notes: subcontractor.notes ?? '',
      isActive: subcontractor.isActive !== false,
    });
    setNotice(null);
  }

  function setField<K extends keyof SubcontractorForm>(
    key: K,
    value: SubcontractorForm[K]
  ) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!activeCompanyId || !canManage || !form.name.trim()) return;

    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saveSubcontractor = httpsCallable<
        {
          companyId: string;
          subcontractorId?: string;
          name: string;
          shortName?: string;
          contactName?: string;
          email?: string;
          phone?: string;
          address?: string;
          notes?: string;
          isActive: boolean;
        },
        { success: boolean; subcontractorId: string }
      >(functions, 'saveSubcontractor');
      await saveSubcontractor({
        companyId: activeCompanyId,
        subcontractorId: editing?.id,
        name: form.name.trim(),
        shortName: form.shortName.trim() || undefined,
        contactName: form.contactName.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        address: form.address.trim() || undefined,
        notes: form.notes.trim() || undefined,
        isActive: form.isActive,
      });
      setNotice(editing ? 'Subcontractor updated.' : 'Subcontractor added.');
      setEditing(null);
      setForm(EMPTY_FORM);
    } catch (e) {
      console.error(e);
      setError((e as Error).message || 'Could not save subcontractor.');
    } finally {
      setSaving(false);
    }
  }

  async function setActive(
    subcontractor: SubcontractorRecord,
    isActive: boolean
  ) {
    if (!activeCompanyId || !canManage) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saveSubcontractor = httpsCallable<
        {
          companyId: string;
          subcontractorId: string;
          name: string;
          shortName?: string;
          contactName?: string;
          email?: string;
          phone?: string;
          address?: string;
          notes?: string;
          isActive: boolean;
        },
        { success: boolean; subcontractorId: string }
      >(functions, 'saveSubcontractor');
      await saveSubcontractor({
        companyId: activeCompanyId,
        subcontractorId: subcontractor.id,
        name: subcontractor.name,
        shortName: subcontractor.shortName ?? undefined,
        contactName: subcontractor.contactName ?? undefined,
        email: subcontractor.email ?? undefined,
        phone: subcontractor.phone ?? undefined,
        address: subcontractor.address ?? undefined,
        notes: subcontractor.notes ?? undefined,
        isActive,
      });
      setNotice(isActive ? 'Subcontractor reactivated.' : 'Subcontractor archived.');
    } catch (e) {
      console.error(e);
      setError((e as Error).message || 'Could not update subcontractor.');
    } finally {
      setSaving(false);
    }
  }

  if (!canManage) {
    return (
      <EmptyState
        title="Subcontractors are admin-managed"
        description="This matches the iOS app: only company admins can maintain suppliers and subcontractor access."
      />
    );
  }

  return (
    <div>
      <PageHeader
        title="Subcontractors"
        description="Recovery firms you hand work to. A subcontractor only ever sees the jobs assigned to them."
        action={<Button type="button" onClick={startNew}>Add subcontractor</Button>}
      />

      <div className="mt-5 grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div>
          <SearchField
            label="Search subcontractors"
            placeholder="Search firm, contact or phone"
            value={search}
            onChange={setSearch}
          />

          <div className="mt-3 space-y-2.5 empty:hidden">
            {error && <Alert tone="error">{error}</Alert>}
            {notice && <Alert tone="success">{notice}</Alert>}
            {readError && <ReadFailure message={readError} retry={() => setReadAttempt((n) => n + 1)} />}
          </div>

          {loading ? (
            <div className="mt-3 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-[14px] border border-hair bg-elev px-4 py-3.5">
                  <div className="skeleton h-4 w-40 rounded-md" />
                  <div className="skeleton mt-2 h-3.5 w-56 rounded-md" />
                </div>
              ))}
            </div>
          ) : readError ? null : subcontractors.length === 0 ? (
            <div className="mt-4">
              <EmptyState
                title="No subcontractors yet"
                description="Add them here so assignment works the same on web and iOS."
              />
            </div>
          ) : (
            <>
              <RecordGroup title="Active" count={activeSubcontractors.length} emptyLabel="No active subcontractors match that search.">
                {activeSubcontractors.map((subcontractor) => (
                  <SubcontractorRow key={subcontractor.id} subcontractor={subcontractor} saving={saving}
                    onEdit={startEdit} onSetActive={() => setActive(subcontractor, false)} />
                ))}
              </RecordGroup>
              {archivedSubcontractors.length > 0 && (
                <RecordGroup title="Archived" count={archivedSubcontractors.length} emptyLabel="No archived subcontractors.">
                  {archivedSubcontractors.map((subcontractor) => (
                    <SubcontractorRow key={subcontractor.id} subcontractor={subcontractor} saving={saving} archived
                      onEdit={startEdit} onSetActive={() => setActive(subcontractor, true)} />
                  ))}
                </RecordGroup>
              )}
            </>
          )}
        </div>

        <FormPanel
          title={editing ? 'Edit subcontractor' : 'Add subcontractor'}
          description="Firms saved here can be assigned to jobs and linked to subcontractor logins."
          className="xl:sticky xl:top-2"
        >
          <form onSubmit={save} className="flex flex-col gap-4 p-5">
            <Field label="Name">
              <TextInput value={form.name} required onChange={(event) => setField('name', event.target.value)}
                placeholder="Mercia Recovery Ltd" />
            </Field>
            <Field label="Nickname" optional>
              <TextInput value={form.shortName} onChange={(event) => setField('shortName', event.target.value)}
                placeholder="Shown on job rows" />
            </Field>
            <Field label="Contact name" optional>
              <TextInput value={form.contactName} onChange={(event) => setField('contactName', event.target.value)} />
            </Field>
            <Field label="Email" optional>
              <TextInput type="email" value={form.email} onChange={(event) => setField('email', event.target.value)} />
            </Field>
            <Field label="Phone" optional>
              <TextInput type="tel" value={form.phone} onChange={(event) => setField('phone', event.target.value)} />
            </Field>
            <Field label="Address" optional>
              <TextArea value={form.address} rows={3} onChange={(event) => setField('address', event.target.value)} />
            </Field>
            <Field label="Notes" optional>
              <TextArea value={form.notes} rows={3} onChange={(event) => setField('notes', event.target.value)} />
            </Field>

            <div className="flex items-center justify-between gap-4 rounded-[10px] bg-surface px-3.5 py-3">
              <span>
                <span className="block text-[15px] font-semibold text-ink">Offer for assignment</span>
                <span className="mt-0.5 block text-[13px] text-ink3">Turn off to archive without losing the record.</span>
              </span>
              <Switch label="Offer for assignment" checked={form.isActive}
                onChange={(checked) => setField('isActive', checked)} />
            </div>

            <div className="flex gap-2">
              <Button type="submit" loading={saving} disabled={saving || !form.name.trim()}>
                {editing ? 'Save changes' : 'Add subcontractor'}
              </Button>
              {editing && <Button type="button" variant="secondary" onClick={startNew}>Cancel</Button>}
            </div>
          </form>
        </FormPanel>
      </div>
    </div>
  );
}

function SubcontractorRow({
  subcontractor, archived = false, saving, onEdit, onSetActive,
}: {
  subcontractor: SubcontractorRecord;
  archived?: boolean;
  saving: boolean;
  onEdit: (subcontractor: SubcontractorRecord) => void;
  onSetActive: () => void;
}) {
  return (
    <div className={`flex flex-wrap items-start justify-between gap-3 rounded-[14px] border border-hair bg-elev px-4 py-3.5 ${archived ? 'opacity-60' : ''}`}>
      <div className="min-w-0">
        <button type="button" onClick={() => onEdit(subcontractor)}
          className="text-left text-[16px] font-semibold text-ink hover:text-accent">
          {subcontractor.shortName || subcontractor.name}
        </button>
        {subcontractor.shortName && subcontractor.shortName !== subcontractor.name && (
          <p className="mt-0.5 text-[14px] text-ink2">{subcontractor.name}</p>
        )}
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[12.5px] text-ink3">
          {subcontractor.contactName && <span>{subcontractor.contactName}</span>}
          {subcontractor.phone && <span className="font-mono">{subcontractor.phone}</span>}
          {subcontractor.updatedAt && <span>Updated <span className="font-mono">{formatTimestamp(subcontractor.updatedAt)}</span></span>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={() => onEdit(subcontractor)}>Edit</Button>
        <Button type="button" variant="secondary" size="sm" disabled={saving} onClick={onSetActive}>
          {archived ? 'Reactivate' : 'Archive'}
        </Button>
      </div>
    </div>
  );
}
